import axios from "axios";
import { Button, Paper } from "@material-ui/core";
import React, { ChangeEvent, useState, useEffect } from "react";
import { RouteComponentProps, withRouter } from "react-router";
import "./index.scss";
import { callAPI } from "services/api";
import RouteURLs from "RouteURLs";
import { Bounds2D } from "util/geometry";
import {
  DesignMediaType,
  DesignSourceType,
  DesignScreenGroup,
} from "services/entities/designs";
import { SpaceScreen } from "services/entities/spaces";
import { readDesignScreenGroup } from "services/designs";
import Dashboard from "layouts/Dashboard";
import { handleError } from "util/errors";

interface EditDesignScreenGroupPageProps
  extends RouteComponentProps<{
    designCode: string;
    screenGroupCode: string;
    mediaType: DesignMediaType;
    screenWidth: string;
    screenHeight: string;
  }> {}

export default withRouter(EditDesignScreenGroupPage);

const IMAGE_FORMATS = ".jpg, .jpeg, .png";
const MAX_IMAGE_SIZE = 20971520;
const VIDEO_FORMATS = ".mp4, .m4v";
const MAX_VIDEO_SIZE = 524288000;
const MB_SIZE = 1048576;

function EditDesignScreenGroupPage(props: EditDesignScreenGroupPageProps) {
  const { designCode, screenGroupCode, screenWidth, screenHeight, mediaType } =
    props.match.params;
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [thumbnail, setThumbnail] = useState("");
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState({ complete: 0, remaining: "" });
  const [fileFormatError, setFormatError] = useState("");
  const [fileFormatWarning, setFormatWarning] = useState("");
  const validationConfig =
    DesignMediaType.Image === mediaType
      ? {
          formats: IMAGE_FORMATS,
          size: MAX_IMAGE_SIZE,
        }
      : {
          formats: VIDEO_FORMATS,
          size: MAX_VIDEO_SIZE,
        };

  useEffect(() => {
    const handleWindowClose = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = "";
      const message = "Are you sure you want to leave?";
      event.returnValue = message;
      return message;
    };
    window.addEventListener("beforeunload", handleWindowClose);
    return () => {
      window.removeEventListener("beforeunload", handleWindowClose);
    };
  });

  const validFileFormat = (fileFormat: string) => {
    const formatsArray = validationConfig.formats.split(", ");
    return formatsArray.includes(`.${fileFormat}`);
  };

  const validFileSize = (fileSize: number) => {
    return fileSize <= validationConfig.size;
  };

  const validateImageResolution = (file: File) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const widthError = img.width !== parseInt(screenWidth);
        const heightError = img.height !== parseInt(screenHeight);
        if (widthError || heightError) {
          setFormatWarning(
            `Image should be width: ${screenWidth} x height: ${screenHeight}. Selected image is: width: ${img.width} x height: ${img.height}.`
          );
        }
      };
      img.src = typeof e?.target?.result === "string" ? e?.target?.result : "";
    };
  };

  const getRotatedScreenBounds = (
    screen: SpaceScreen,
    includeBezels: boolean = true
  ): Bounds2D => {
    const bounds: Bounds2D = screen.bounds;
    const bezels = screen.bezels;
    if (screen.rotation === 0) {
      if (includeBezels) {
        return bounds;
      } else {
        return {
          x: bounds.x + bezels.left,
          y: bounds.y + bezels.top,
          width: bounds.width - bezels.left - bezels.right,
          height: bounds.height - bezels.top - bezels.bottom,
        };
      }
    }
    const width = includeBezels
      ? bounds.width
      : bounds.width - bezels.left - bezels.right;
    const height = includeBezels
      ? bounds.height
      : bounds.height - bezels.top - bezels.bottom;
    const rotationRadians = (screen.rotation * Math.PI) / 180.0;
    const rotationCos = Math.cos(rotationRadians);
    const rotationSin = Math.sin(rotationRadians);
    const offsetX = !includeBezels
      ? rotationCos * bezels.left - rotationSin * bezels.top
      : 0;
    const offsetY = !includeBezels
      ? rotationSin * bezels.left + rotationCos * bezels.top
      : 0;
    const topLeft = { x: offsetX, y: offsetY };
    const topRight = {
      x: offsetX + width * rotationCos,
      y: offsetY + width * rotationSin,
    };
    const bottomLeft = {
      x: offsetX - height * rotationSin,
      y: offsetY + height * rotationCos,
    };
    const bottomRight = {
      x: offsetX + width * rotationCos - height * rotationSin,
      y: offsetY + width * rotationSin + height * rotationCos,
    };
    const xMin = Math.min(topLeft.x, topRight.x, bottomLeft.x, bottomRight.x);
    const xMax = Math.max(topLeft.x, topRight.x, bottomLeft.x, bottomRight.x);
    const yMin = Math.min(topLeft.y, topRight.y, bottomLeft.y, bottomRight.y);
    const yMax = Math.max(topLeft.y, topRight.y, bottomLeft.y, bottomRight.y);
    return {
      x: bounds.x + xMin,
      y: bounds.y + yMin,
      width: xMax - xMin,
      height: yMax - yMin,
    };
  };

  const getSelectedScreenBounds = (screenGroup: DesignScreenGroup) => {
    if (screenGroup) {
      const screens = screenGroup.screenGroup.screens;
      let videoBounds: Bounds2D | null = null;
      let offsetX = 0;
      let offsetY = 0;
      for (let k = 0; k < screens.length; k++) {
        const screen = screens[k];
        const screenBounds = getRotatedScreenBounds(screen, false);
        if (!videoBounds) {
          videoBounds = screenBounds;
        } else {
          const xMin = Math.min(videoBounds.x, screenBounds.x);
          const xMax = Math.max(
            videoBounds.x + videoBounds.width,
            screenBounds.x + screenBounds.width
          );
          const yMin = Math.min(videoBounds.y, screenBounds.y);
          const yMax = Math.max(
            videoBounds.y + videoBounds.height,
            screenBounds.y + screenBounds.height
          );
          videoBounds.x = xMin;
          videoBounds.y = yMin;
          videoBounds.width = xMax - xMin;
          videoBounds.height = yMax - yMin;
        }
        if (k === 0) {
          offsetX = screenBounds.x;
          offsetY = screenBounds.y;
        } else {
          offsetX = Math.min(offsetX, screenBounds.x);
          offsetY = Math.min(offsetY, screenBounds.y);
        }
      }
      if (!videoBounds) {
        throw new Error("No screens selected");
      }
      // offset by top left bezel
      videoBounds.x -= offsetX;
      videoBounds.y -= offsetY;
      return videoBounds;
    }
  };

  const createVideoThumbnail = (file: File) => {
    const video = document.createElement("video") as HTMLVideoElement;
    const canvas = document.createElement("canvas") as HTMLCanvasElement;
    video.addEventListener("loadedmetadata", () => {
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      video.currentTime = 1;
      video.addEventListener("seeked", () => {
        const context = canvas.getContext("2d");
        context?.drawImage(video, 0, 0, canvas.width, canvas.height);
        const dataUrl = canvas.toDataURL();
        setThumbnail(dataUrl);
      });
    });
    video.src = URL.createObjectURL(file);
  };

  const base64ToBlob = (base64: string) => {
    const base64Split = base64.split(",");
    const byteCharacters = atob(base64Split[base64Split.length - 1]);
    const byteArrays = [];
    for (let offset = 0; offset < byteCharacters.length; offset += 512) {
      const slice = byteCharacters.slice(offset, offset + 512);
      const byteNumbers = new Array(slice.length);
      for (let i = 0; i < slice.length; i++) {
        byteNumbers[i] = slice.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      byteArrays.push(byteArray);
    }
    return new Blob(byteArrays, { type: "image/png" });
  };

  const handleFileSelect = (event: ChangeEvent<HTMLInputElement>) => {
    setFormatError("");
    setFormatWarning("");
    const file = event.target.files?.[0];
    if (file) {
      const fileExtension = file.type.split("/")[1];
      if (!validFileFormat(fileExtension)) {
        setFormatError(
          `The ${mediaType} should be one of ${validationConfig.formats}. Selected ${mediaType} is: ${fileExtension}`
        );
      }
      if (!validFileSize(file.size)) {
        setFormatError(
          `The ${mediaType} should be less than ${validationConfig.size} bytes. Selected ${mediaType} is: ${file.size}`
        );
      }
      if (mediaType === DesignMediaType.Video) {
        createVideoThumbnail(file);
      }
      if (mediaType === DesignMediaType.Image) {
        validateImageResolution(file);
      }
      setSelectedFile(file);
    }
  };

  const getS3Url = async (file: File | Blob, thumbnail = false) => {
    const fileExtension = thumbnail ? "png" : file.type.split("/")[1];
    const fileName = `${designCode}_${screenGroupCode}_${Date.now()}${
      thumbnail ? "_thumbail" : ""
    }.${fileExtension}`;
    const { url } = await callAPI(
      `/designs/${designCode}/screen-groups/${screenGroupCode}/file-upload-url/${fileName}/`,
      "GET",
      null,
      true
    );
    return { url, fileName };
  };

  const uploadFile = async (
    url: string,
    file: File | Blob,
    thumbnail = false
  ) => {
    await axios.request({
      method: "PUT",
      url,
      data: file,
      headers: {
        "Content-Type": file.type,
      },
      ...(thumbnail
        ? {}
        : {
            onUploadProgress: (p) => {
              let remaining = "";
              if (p.estimated) {
                const minutes = Math.floor(p.estimated / 60);
                const seconds = Math.round(p.estimated - minutes * 60);
                remaining = `${minutes} min ${seconds} secs`;
              }
              setProgress({
                complete: Math.round((p.progress || 0) * 100),
                remaining,
              });
            },
          }),
    });
  };

  const updateScreenGroup = async (
    fileName: string,
    thumbnailFilename?: string,
    designGroup?: DesignScreenGroup
  ) => {
    const MAX_RETRIES = 5;
    let retries = 0;
    const fileKey = `designs/${designCode}/${screenGroupCode}/${fileName}`;
    while (retries < MAX_RETRIES) {
      const requestData = {
        file_key: fileKey,
        file_type: mediaType,
        ...(designGroup &&
          thumbnailFilename && {
            thumbnail_key: `designs/${designCode}/${screenGroupCode}/${thumbnailFilename}`,
            bounds: getSelectedScreenBounds(designGroup),
          }),
      };
      await callAPI(
        `/designs/${designCode}/${mediaType}/${screenGroupCode}/`,
        "PUT",
        requestData,
        true
      );
      const updatedScreenGroup = await callAPI(
        `/designs/${designCode}/screen-groups/${screenGroupCode}`,
        "GET",
        null,
        true
      );
      const readDesignGroup = readDesignScreenGroup(
        updatedScreenGroup,
        DesignSourceType.File
      );
      if (readDesignGroup.sourceData.source_file_key === fileKey) {
        break;
      }
      retries++;
      console.error(
        `Attempt ${retries + 1} failed: "design failed to update."`
      );
      if (retries === MAX_RETRIES) {
        throw new Error("Max attempts at saving design. Please try again.");
      }
    }
  };

  const processImage = async (file: File) => {
    const { url, fileName } = await getS3Url(file);
    await uploadFile(url, file);
    await updateScreenGroup(fileName);
  };

  const processVideo = async (file: File) => {
    const thumbnailFile = base64ToBlob(thumbnail);
    const [videoUrl, thumbnailUrl] = await Promise.all([
      getS3Url(file),
      getS3Url(thumbnailFile, true),
    ]);
    const [response] = await Promise.all([
      callAPI(
        `/designs/${designCode}/screen-groups/${screenGroupCode}`,
        "GET",
        null,
        true
      ),
      uploadFile(videoUrl.url, file),
      uploadFile(thumbnailUrl.url, thumbnailFile, true),
    ]);
    const designGroup = readDesignScreenGroup(response, DesignSourceType.File);
    await updateScreenGroup(
      videoUrl.fileName,
      thumbnailUrl.fileName,
      designGroup
    );
  };

  const handleSubmission = async () => {
    if (!selectedFile || fileFormatError) {
      return;
    }
    try {
      setUploading(true);
      await (DesignMediaType.Image === mediaType
        ? processImage(selectedFile)
        : processVideo(selectedFile));
      props.history.push(RouteURLs.DESIGN_LIST);
    } catch (err) {
      handleError("Failed to upload media to design.", err);
    }
  };

  return (
    <Dashboard id="edit-design-screen-group-page">
      <Paper className="upload-section">
        {uploading ? (
          <div>
            <p>Uploading: {progress.complete}%</p>
            <div className="upload-section__progress-container">
              <div
                className="upload-section__progress-bar"
                style={{ width: progress.complete + "%" }}
              ></div>
            </div>
            {progress.remaining && <p>Remaining: {progress.remaining}</p>}
          </div>
        ) : (
          <div>
            <input
              type="file"
              accept={validationConfig.formats}
              name="file"
              id="file"
              className="inputfile"
              onChange={handleFileSelect}
            />
            <label htmlFor="file">{`Choose ${
              selectedFile ? "another" : "a"
            } file`}</label>
            {selectedFile ? (
              <div className="upload-section__file-info">
                <p className="upload-section__file-info__item">
                  <b>Selected File:</b> {selectedFile.name}
                </p>
                {fileFormatWarning && (
                  <div>
                    <p className="upload-section__format-warning">
                      {fileFormatWarning}
                    </p>
                  </div>
                )}
                {fileFormatError ? (
                  <div>
                    <p className="upload-section__format-error">
                      {fileFormatError}
                    </p>
                  </div>
                ) : (
                  <div className="upload-section__upload-button">
                    <Button
                      className="upload-section__upload-button__button"
                      onClick={handleSubmission}
                      variant="contained"
                    >
                      Upload
                    </Button>
                  </div>
                )}
              </div>
            ) : (
              <div className="upload-section__file-info">
                <p className="upload-section__file-info__item">
                  Select a file to upload
                </p>
                <p className="upload-section__file-info__item">
                  Supported formats: {validationConfig.formats}
                </p>
                <p className="upload-section__file-info__item">
                  Max file size: {validationConfig.size / MB_SIZE} MB
                </p>
                <p className="upload-section__file-info__item">
                  {`Screen resolution: ${screenWidth} x ${screenHeight}`}
                </p>
              </div>
            )}
          </div>
        )}
      </Paper>
    </Dashboard>
  );
}
